// src/components/search/SearchForm.tsx
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import StreetAutocomplete, { type StreetResult } from "@/pages/StreetAutocomplete";
import { NO_STATUS_VALUE, STATUS_OPTIONS } from "@/search/constants";

interface SearchFormProps {
  streetInput: string;
  postcodeInput: string;
  statusInput: string[];
  onStreetChange: (value: string) => void;
  onPostcodeChange: (value: string) => void;
  onStatusChange: (value: string[]) => void;
  onStreetSelect: (street: StreetResult | null) => void;
  onSearch: () => void;
  onClear: () => void;
  enableAutocomplete: boolean;
}

export default function SearchForm({
  streetInput,
  postcodeInput,
  statusInput,
  onStreetChange,
  onPostcodeChange,
  onStatusChange,
  onStreetSelect,
  onSearch,
  onClear,
  enableAutocomplete,
}: SearchFormProps) {
  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
        <Stack spacing={1.5}>
          <StreetAutocomplete
            streetInput={streetInput}
            setStreetInput={onStreetChange}
            enableAutocomplete={enableAutocomplete}
            onEnter={onSearch}
            onStreetSelect={onStreetSelect}
          />

          <Grid container spacing={1}>
            <Grid size={6}>
              <TextField
                label="Postcode"
                value={postcodeInput}
                size="small"
                fullWidth
                onChange={(e) => onPostcodeChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
              />
            </Grid>

            <Grid size={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statusInput}
                  onChange={(e) => onStatusChange(e.target.value as string[])}
                  label="Status"
                  size="small"
                  renderValue={(selected) =>
                    selected
                      .map((v) =>
                        v === NO_STATUS_VALUE
                          ? "No Status"
                          : STATUS_OPTIONS.find((o) => o.value === v)?.label
                      )
                      .join(", ")
                  }
                >
                  <MenuItem value={NO_STATUS_VALUE}>
                    <em>No Status</em>
                  </MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Button variant="contained" fullWidth onClick={onSearch}>
            Search
          </Button>

          <Button fullWidth variant="outlined" onClick={onClear}>
            Clear
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
